import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import DocumentThumbnail from '@/components/feature/DocumentThumbnail';

interface ReviewDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  documentId: number | null;
  documentName: string;
  mimeType?: string;
}

export default function ReviewDocumentModal({ isOpen, onClose, onSuccess, documentId, documentName, mimeType }: ReviewDocumentModalProps) {
  const { accessToken, user } = useAuth();
  const [action, setAction] = useState<'verified' | 'failed' | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!isOpen || !documentId) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!action) { setError('Please select Approve or Disapprove'); return; }
    if (action === 'failed' && !reason.trim()) { setError('Reason is required when disapproving'); return; }

    setSubmitting(true);
    setError('');

    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL || 'https://ekyc.tumkurconnect.com/api/v1'}/documents/${documentId}/review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ status: action, reason: action === 'failed' ? reason.trim() : null }),
      });
      const json = await res.json();
      if (!res.ok) {
        setError(json.message || 'Review failed');
        setSubmitting(false);
        return;
      }
      onSuccess();
      onClose();
      setAction(null);
      setReason('');
    } catch {
      setError('Network error during review');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center gap-3 mb-4">
          <DocumentThumbnail
            documentId={documentId ?? 0}
            mimeType={mimeType || ''}
            fileName={documentName}
            size="lg"
            rounded="xl"
          />
          <div className="min-w-0">
            <h4 className="text-lg font-bold text-stone-900 truncate">Review Document</h4>
            <p className="text-stone-400 text-xs truncate">{documentName}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => { setAction('verified'); setError(''); }}
              className={`flex-1 py-3 rounded-xl border text-sm font-semibold transition-colors ${
                action === 'verified'
                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                  : 'border-stone-200 text-stone-600 hover:bg-stone-50'
              }`}
            >
              <i className="ri-check-line mr-1" />
              Approve
            </button>
            <button
              type="button"
              onClick={() => { setAction('failed'); setError(''); }}
              className={`flex-1 py-3 rounded-xl border text-sm font-semibold transition-colors ${
                action === 'failed'
                  ? 'border-red-500 bg-red-50 text-red-700'
                  : 'border-stone-200 text-stone-600 hover:bg-stone-50'
              }`}
            >
              <i className="ri-close-line mr-1" />
              Disapprove
            </button>
          </div>

          {action === 'failed' && (
            <div>
              <label className="block text-stone-700 text-sm font-medium mb-1.5">Reason for Disapproval *</label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Enter reason..."
                rows={3}
                maxLength={500}
                className="w-full border border-stone-200 rounded-lg px-3 py-2.5 text-sm text-stone-900 focus:outline-none focus:border-brand-400 focus:ring-1 focus:ring-brand-400 resize-none"
              />
              <p className="text-stone-300 text-[10px] text-right mt-0.5">{reason.length}/500</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-100 rounded-lg px-3 py-2 text-red-600 text-xs">
              {error}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-2.5 rounded-full border border-stone-200 text-stone-600 font-medium text-sm hover:bg-stone-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="flex-1 bg-brand-600 hover:bg-brand-700 disabled:bg-brand-300 text-white font-semibold py-2.5 rounded-full text-sm transition-colors flex items-center justify-center gap-2"
            >
              {submitting && <i className="ri-loader-4-line animate-spin" />}
              Submit Review
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}